// src/Chat.tsx
import React, { useState, useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import ChatHeader from "./ChatHeader.tsx";
import MessageList from "./MessageList.tsx";
import ChatInput from "./ChatInput.tsx";
import TypingIndicator from "./TypingIndicator.tsx";

const socket: Socket = io("https://gulpies-chat.onrender.com", {
  transports: ["websocket"],
});

interface Message {
  sender: string;
  text: string;
  timestamp?: string;
}

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [username] = useState("User" + Math.floor(Math.random() * 1000));
  const [darkMode, setDarkMode] = useState(false);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const audioRef = useRef<HTMLAudioElement>(null);

  const theme = {
    light: {
      bg: "#fff",
      msgOwn: "#dbeafe",
      msgOther: "#e0e7ff",
      text: "#000",
      headerBg: "#4f46e5",
      headerText: "#fff",
      inputBg: "#f9f9f9",
    },
    dark: {
      bg: "#1f2937",
      msgOwn: "#374151",
      msgOther: "#4b5563",
      text: "#f9fafb",
      headerBg: "#312b94ff",
      headerText: "#f9fafb",
      inputBg: "#374151",
    },
  };
  const colors = darkMode ? theme.dark : theme.light;

  useEffect(() => {
    socket.on("connect", () => console.log("Connected to backend"));

    socket.on("receive_message", (msg: Message) => {
      setMessages((prev) => [...prev, msg]);
      audioRef.current?.play();
    });

    socket.on("user_typing", (user: string) => {
      if (user !== username && !typingUsers.includes(user)) {
        setTypingUsers((prev) => [...prev, user]);
      }
    });

    socket.on("stop_typing", (user: string) => {
      setTypingUsers((prev) => prev.filter((u) => u !== user));
    });

    return () => {
      socket.off("receive_message");
      socket.off("user_typing");
      socket.off("stop_typing");
    };
  }, [username, typingUsers]);

  const sendMessage = () => {
    if (input.trim()) {
      const message: Message = {
        sender: username,
        text: input,
        timestamp: new Date().toISOString(),
      };
      socket.emit("send_message", message);
      setInput("");
      socket.emit("stop_typing", username);
    }
  };

  const handleTyping = (val: string) => {
    setInput(val);
    if (val.length > 0) socket.emit("user_typing", username);
    else socket.emit("stop_typing", username);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        borderRadius: 12,
        overflow: "hidden",
        backgroundColor: colors.bg, // only chat box
        color: colors.text,
        width: "100%",
        height: "98vh", // full screen height
      }}
    >
      <ChatHeader darkMode={darkMode} setDarkMode={setDarkMode} colors={colors} />
      <div
        style={{
          flexGrow: 1, // fill remaining space
          overflowY: "auto",
          padding: 20,
        }}
      >
        <MessageList messages={messages} username={username} colors={colors} />
      </div>
      <TypingIndicator typingUsers={typingUsers} />
      <ChatInput input={input} setInput={handleTyping} sendMessage={sendMessage} colors={colors} />
      <audio ref={audioRef} src="/notification.mp3" />
    </div>
  );
};

export default Chat;
